import { Controller } from "@hotwired/stimulus"

const ROW_SELECTOR = '[data-notifications-inbox-target~="row"]'

// The notifications inbox: row selection with the bulk bar, and delete with undo.
//
// A delete hides the rows and shows a toast; the request goes out when the toast
// closes, or right away when the page is left or another action is submitted. Only
// one delete waits at a time — a second one sends the first. The server answers with a
// morphing refresh, which drops the rows for good and corrects every count. Rows whose
// delete is on its way stay hidden until it fails: a refresh from another action can be
// rendered before the delete has landed, and would otherwise morph them back.
export default class extends Controller {
  static targets = [
    "row",
    "checkbox",
    "selectAll",
    "selectedCount",
    "group",
    "empty",
    "toast",
    "toastMessage",
  ]
  static values = {
    bulkDeleteUrl: String,
    deletedOne: String,
    deletedOther: String,
    selected: String,
    undoMs: { type: Number, default: 6000 },
  }

  connect() {
    this.pending = null
    this.sending = new Set()
    this.sendPending = () => this.commit({ render: false })
    this.sync = this.sync.bind(this)
    document.addEventListener("turbo:before-visit", this.sendPending)
    document.addEventListener("turbo:submit-start", this.sendPending)
    document.addEventListener("turbo:morph", this.sync)
    window.addEventListener("pagehide", this.sendPending)
    this.sync()
  }

  disconnect() {
    this.commit({ render: false })
    document.removeEventListener("turbo:before-visit", this.sendPending)
    document.removeEventListener("turbo:submit-start", this.sendPending)
    document.removeEventListener("turbo:morph", this.sync)
    window.removeEventListener("pagehide", this.sendPending)
  }

  toggle() {
    this.sync()
  }

  toggleAll() {
    const checked = this.selectAllTarget.checked
    this.visibleRows().forEach((row) => {
      const box = this.checkboxOf(row)
      if (box) box.checked = checked
    })
    this.sync()
  }

  clear() {
    this.checkboxTargets.forEach((box) => (box.checked = false))
    this.sync()
  }

  delete(event) {
    const row = event.currentTarget.closest(ROW_SELECTOR)
    if (!row) return

    this.schedule([row], { url: event.currentTarget.dataset.url, method: "DELETE" })
  }

  bulkDelete() {
    const rows = this.selectedRows()
    if (rows.length === 0) return

    const body = new FormData()
    rows.forEach((row) => body.append("ids[]", row.dataset.notificationId))
    this.schedule(rows, { url: this.bulkDeleteUrlValue, method: "PATCH", body })
  }

  undo() {
    if (!this.pending) return

    clearTimeout(this.timer)
    const ids = this.pending.ids
    this.pending = null
    this.rowsById(ids).forEach((row) => (row.hidden = false))
    this.toastTarget.hidden = true
    this.sync()
  }

  // private

  schedule(rows, request) {
    this.commit({ render: false })

    rows.forEach((row) => {
      row.hidden = true
      const box = this.checkboxOf(row)
      if (box) box.checked = false
    })
    this.pending = { ids: rows.map((row) => row.dataset.notificationId), ...request }
    this.toastMessageTarget.textContent =
      rows.length === 1
        ? this.deletedOneValue
        : this.deletedOtherValue.replace("__COUNT__", rows.length)
    this.toastTarget.hidden = false
    this.timer = setTimeout(() => this.commit({ render: true }), this.undoMsValue)
    this.sync()
  }

  commit({ render }) {
    const pending = this.pending
    if (!pending) return

    this.pending = null
    clearTimeout(this.timer)
    if (this.hasToastTarget) this.toastTarget.hidden = true
    pending.ids.forEach((id) => this.sending.add(id))

    fetch(pending.url, {
      method: pending.method,
      body: pending.body,
      keepalive: true,
      credentials: "same-origin",
      headers: {
        Accept: "text/vnd.turbo-stream.html",
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || "",
      },
    })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`)
        return response.text()
      })
      .then((html) => {
        if (render) window.Turbo?.renderStreamMessage(html)
      })
      .catch(() => {
        pending.ids.forEach((id) => this.sending.delete(id))
        this.rowsById(pending.ids).forEach((row) => (row.hidden = false))
        this.sync()
      })
  }

  // Recomputes everything derived from the DOM: the selection state the bulk bar reads,
  // the master checkbox, empty date groups and the empty state. Also runs after a morph,
  // which re-renders rows a waiting or travelling delete had hidden.
  sync() {
    const hiddenIds = [...this.sending, ...(this.pending?.ids || [])]
    this.rowsById(hiddenIds).forEach((row) => (row.hidden = true))

    const visible = this.visibleRows()
    const selected = this.selectedRows()
    this.element.dataset.selecting = selected.length > 0 ? "true" : "false"

    if (this.hasSelectedCountTarget) {
      this.selectedCountTarget.textContent = this.selectedValue.replace(
        "__COUNT__",
        selected.length,
      )
    }
    if (this.hasSelectAllTarget) {
      this.selectAllTarget.checked = selected.length > 0 && selected.length === visible.length
      this.selectAllTarget.indeterminate = selected.length > 0 && selected.length < visible.length
    }
    this.groupTargets.forEach((group) => {
      group.hidden = !group.querySelector(`${ROW_SELECTOR}:not([hidden])`)
    })
    if (this.hasEmptyTarget) this.emptyTarget.classList.toggle("hidden", visible.length > 0)
  }

  visibleRows() {
    return this.rowTargets.filter((row) => !row.hidden)
  }

  selectedRows() {
    return this.visibleRows().filter((row) => this.checkboxOf(row)?.checked)
  }

  rowsById(ids) {
    return this.rowTargets.filter((row) => ids.includes(row.dataset.notificationId))
  }

  checkboxOf(row) {
    return row.querySelector('[data-notifications-inbox-target~="checkbox"]')
  }
}
